(() => {
  const ROOT_SELECTOR = '[data-venada-testimonials-carousel]';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VenadaTestimonials {
    constructor(root) {
      this.root = root;
      this.scroller = root.querySelector('[data-venada-testimonials-scroller]');
      this.cards = Array.from(root.querySelectorAll('[data-venada-testimonials-image]'));
      this.quoteCard = root.querySelector('[data-venada-testimonials-quote-card]');
      this.quote = root.querySelector('[data-venada-testimonials-quote]');
      this.name = root.querySelector('[data-venada-testimonials-name]');
      this.badge = root.querySelector('[data-venada-testimonials-badge]');
      this.verifiedText = root.querySelector('[data-venada-testimonials-verified-text]');
      this.progress = root.querySelector('[data-venada-testimonials-progress]');
      this.progressBar = root.querySelector('[data-venada-testimonials-progress-bar]');
      this.progressHitarea = root.querySelector('[data-venada-testimonials-progress-hitarea]');
      this.previousButton = root.querySelector('[data-venada-testimonials-prev]');
      this.nextButton = root.querySelector('[data-venada-testimonials-next]');
      this.status = root.querySelector('[data-venada-testimonials-status]');
      this.activeIndex = 0;
      this.count = this.cards.length;
      this.fadeTimer = null;
      this.observer = null;
    }

    init() {
      if (!this.scroller || this.count === 0 || this.root.dataset.venadaTestimonialsInitialized === 'true') return;

      this.root.dataset.venadaTestimonialsInitialized = 'true';
      this.root.style.setProperty('--venada-testimonials-progress-width', `${100 / this.count}%`);

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.goTo(Number(card.dataset.index || 0)));
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          this.goTo(Number(card.dataset.index || 0));
        });
      });

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      this.progressHitarea?.addEventListener('click', (event) => {
        const bounds = this.progressHitarea.getBoundingClientRect();
        const ratio = Math.min(0.999, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        this.goTo(Math.floor(ratio * this.count));
      });

      this.setupObserver();
      this.updateState(0, false);
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.55)) return;

          const visibleEntry = this.getMostVisibleCard();

          if (!visibleEntry || visibleEntry.ratio < 0.55) return;

          this.updateState(visibleEntry.index);
        },
        {
          root: this.scroller,
          threshold: [0.55, 0.7, 0.85, 1],
        }
      );

      this.cards.forEach((card) => this.observer.observe(card));
    }

    getMostVisibleCard() {
      const scrollerRect = this.scroller.getBoundingClientRect();

      return this.cards
        .map((card, index) => {
          const rect = card.getBoundingClientRect();
          const visibleWidth = Math.max(0, Math.min(rect.right, scrollerRect.right) - Math.max(rect.left, scrollerRect.left));
          const visibleHeight = Math.max(0, Math.min(rect.bottom, scrollerRect.bottom) - Math.max(rect.top, scrollerRect.top));
          const area = rect.width * rect.height || 1;

          return {
            index,
            ratio: (visibleWidth * visibleHeight) / area,
          };
        })
        .sort((a, b) => b.ratio - a.ratio)[0];
    }

    goTo(index) {
      const nextIndex = (index + this.count) % this.count;
      const card = this.cards[nextIndex];

      if (!card) return;

      card.scrollIntoView({
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });

      this.updateState(nextIndex);
    }

    updateState(index, announce = true) {
      if (index === this.activeIndex && announce) return;

      this.activeIndex = index;

      this.cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === index;
        card.classList.toggle('is-active', isActive);
        card.dataset.active = isActive ? 'true' : 'false';
        card.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.updateQuote(this.cards[index]);
      this.updateProgress();

      if (this.status && announce) {
        this.status.textContent = `Testimonial ${index + 1} of ${this.count}`;
      }
    }

    updateQuote(card) {
      if (!card || !this.quote || !this.name) return;

      const verified = card.dataset.verified === 'true';
      const writeQuote = () => {
        this.quote.innerHTML = card.dataset.quote || '';
        this.name.textContent = card.dataset.customerName || 'Customer';

        if (this.badge) this.badge.hidden = !verified;
        if (this.verifiedText) this.verifiedText.hidden = !verified;

        this.quoteCard?.classList.remove('is-changing');
      };

      window.clearTimeout(this.fadeTimer);

      if (reducedMotionQuery.matches || !this.quoteCard) {
        writeQuote();
        return;
      }

      this.quoteCard.classList.add('is-changing');
      this.fadeTimer = window.setTimeout(writeQuote, 120);
    }

    updateProgress() {
      if (!this.progress || this.count === 0) return;

      this.progress.style.transform = `translateX(${this.activeIndex * 100}%)`;
      this.progressBar?.setAttribute('aria-valuenow', String(this.activeIndex + 1));
    }
  }

  const initVenadaTestimonials = (root = document) => {
    root.querySelectorAll(ROOT_SELECTOR).forEach((carousel) => {
      new VenadaTestimonials(carousel).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initVenadaTestimonials());
  } else {
    initVenadaTestimonials();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initVenadaTestimonials(event.target);
  });
})();
